import { Dispatch } from 'react';
import { getAutoCompleteRows } from 'src/services/components/vigo-autocomplete';
import { getMasterDataRow } from 'src/services/masterdata';
import { getMasterDataTables } from 'src/services/systemconfig';

export function setInitialDropdownValue(
  value: any,
  serviceEndpoint: string,
  serviceLocation: string,
  sort: string,
  tenantId: string | undefined,
  partnerId: string | undefined,
  setOptionObjects: Dispatch<any[]>,
  setAutocompleteValue: Dispatch<string>,
  setInitialValue: Dispatch<string>,
  setInitialValueObject: Dispatch<any>,
  vigoToast: any
) {
  if (serviceEndpoint == '' || serviceLocation == '') {
    return;
  }

  getMasterDataRow(
    {
      serviceLocation: serviceLocation,
      endPoint: serviceEndpoint,
      id: value,
      sort: sort
    },
    tenantId,
    partnerId
  )
    .then(rowresponse => {
      const rowArray: any[] = [];
      rowArray.push(rowresponse.data);
      setOptionObjects(rowArray);
      setAutocompleteValue(rowresponse.data.id);
      setInitialValue(rowresponse.data.id);
      setInitialValueObject(rowresponse.data);
    })
    .catch(err => {
      vigoToast.error(err.message);
    });
}

export function getMasterDataTableId(
  allowAddNewOption: boolean,
  serviceEndpoint: string,
  serviceLocation: string,
  setMasterDataTableId: Dispatch<string>,
  vigoToast: any
) {
  if (allowAddNewOption) {
    if (serviceEndpoint == '' || serviceLocation == '') {
      return;
    }

    getMasterDataTables({
      filter: "ServiceLocation eq '" + serviceLocation + "'" + " and EndPoint eq '" + serviceEndpoint + "'"
    })
      .then(tableresponse => {
        setMasterDataTableId(tableresponse.data[0].id!);
      })
      .catch(err => {
        vigoToast.error(err.message);
        setMasterDataTableId('');
      });
  }
}

/**
 * Determine the display value for the input when an option has been selected.
 * - If a displayName has been specified and an optionsObject is found with a key of displayname.
 * - Else, if displayColumns have been specified and an optionsObject is found.
 * - Else, if an optionsObject is found and has a key of 'name'.
 * - Else, if an optionsObject is found and has a key of 'id'
 * - Finally, if no optionsObject was found, return the option parameter.
 * @param option
 * @returns
 */
export function getDisplayValue(
  option: string,
  optionObjects: any[],
  displayName: string | undefined,
  displayColumns: string[] | undefined,
  mapColumnValue?: (columnName: string, value: any, rowData: any) => any
) {
  const optionObject = optionObjects.find(x => x.id == option);

  if (displayName && optionObject) {
    displayName = displayName.charAt(0).toLowerCase() + displayName.slice(1);

    if (optionObject.hasOwnProperty(displayName)) {
      return optionObject[displayName];
    }
  }

  if (displayColumns && optionObject) {
    let customOptionDisplay = '';

    for (let index = 0; index < displayColumns.length; index++) {
      const columnName = displayColumns[index];
      const rawValue = optionObject[columnName];
      const displayValue = mapColumnValue ? mapColumnValue(columnName, rawValue, optionObject) : rawValue;

      if (customOptionDisplay == '') {
        customOptionDisplay = displayValue;
        continue;
      }
      customOptionDisplay = customOptionDisplay + ' - ' + displayValue;
    }

    return customOptionDisplay;
  }

  if (optionObject) {
    if (optionObject?.hasOwnProperty('name')) {
      return optionObject['name'];
    } else {
      return optionObject['id'];
    }
  } else {
    return option;
  }
}

export function buildFilter(
  queryText: string | undefined,
  useQueryTextAsFilter: boolean | undefined,
  queryColumns: string[] | undefined,
  displayName: string | undefined,
  inputValue: string | undefined,
  datasourceFilter: string | undefined,
  evaluateFilter: ((filter: string) => string) | undefined
) {
  if (useQueryTextAsFilter && queryText != undefined && queryText != '') {
    return queryText;
  }

  if (useQueryTextAsFilter && (queryText == undefined || queryText == '')) {
    // Just so it doesn't crash with empty string or undefined filter
    return '[]';
  }

  let customFilter = '';
  if (queryColumns !== undefined && queryText !== undefined) {
    for (let index = 0; index < queryColumns.length; index++) {
      if (customFilter == '') {
        customFilter = queryColumns[0] + " li '%" + queryText + "%'";
        continue;
      }
      customFilter = customFilter + ' or ' + queryColumns[index] + " li '%" + queryText + "%'";
    }
  }
  if (queryColumns == undefined && queryText !== undefined) {
    customFilter = (displayName ?? 'description') + " li '%" + queryText + "%'";
  }
  if (queryText == '') {
    customFilter = '';
  }

  if (!queryText && inputValue) {
    if (queryColumns !== undefined && inputValue !== undefined) {
      for (let index = 0; index < queryColumns.length; index++) {
        if (customFilter == '') {
          customFilter = queryColumns[0] + " li '%" + inputValue + "%'";
          continue;
        }
        customFilter = customFilter + ' or ' + queryColumns[index] + " li '%" + inputValue + "%'";
      }
    }
    if (queryColumns == undefined && inputValue !== undefined) {
      customFilter = (displayName ?? 'description') + " li '%" + inputValue + "%'";
    }
    if (inputValue == '') {
      customFilter = '';
    }
  }

  if ((!datasourceFilter || datasourceFilter == '') && customFilter == '') {
    return undefined;
  }
  if (datasourceFilter == '' && customFilter != '') {
    return customFilter;
  }
  if (datasourceFilter != undefined && customFilter != '') {
    if (datasourceFilter.includes('[')) {
      return evaluateFilter ? evaluateFilter(datasourceFilter) : '' + ' and ' + customFilter;
    }

    return `${datasourceFilter} and (${customFilter})`;
  }
  if (datasourceFilter != undefined && customFilter == '') {
    if (datasourceFilter.includes('[')) {
      return evaluateFilter ? evaluateFilter(datasourceFilter) : '';
    }

    return datasourceFilter;
  }
  if (datasourceFilter == undefined && customFilter != '') {
    return customFilter;
  }

  return '';
}

/**
 * Fired when user starts typing
 */
export function fetchAutocompleteRows(
  queryText: string | undefined,
  serviceEndpoint: string,
  serviceLocation: string,
  setAutocompleteOptions: Dispatch<any[]>,
  setLoading: Dispatch<boolean>,
  useQueryTextAsFilter: boolean | undefined,
  queryColumns: string[] | undefined,
  displayName: string | undefined,
  inputValue: string | undefined,
  datasourceFilter: string | undefined,
  evaluateFilter: ((filter: string) => string) | undefined,
  sort: string,
  tenantId: string | undefined,
  partnerId: string | undefined,
  setCanLoadMore: Dispatch<boolean>,
  setCurrentPageNumber: Dispatch<number>,
  initialValue: string | null,
  initialValueObject: any | null,
  setOptionObjects: Dispatch<any[]>,
  vigoToast: any,
  PAGE_SIZE: number
) {
  if (serviceEndpoint == '' || serviceLocation == '') {
    return;
  }

  if (queryText == undefined) {
    setAutocompleteOptions([]);

    return;
  }

  setLoading(true);

  getAutoCompleteRows(
    {
      endPoint: serviceEndpoint,
      serviceLocation: serviceLocation,
      filter: buildFilter(
        queryText,
        useQueryTextAsFilter,
        queryColumns,
        displayName,
        inputValue,
        datasourceFilter,
        evaluateFilter
      ),
      sort: sort,
      pageSize: PAGE_SIZE,
      pageNumber: 0,
      includeMetadata: true
    },
    tenantId,
    partnerId
  )
    .then(rowsresponse => {
      if ((rowsresponse.data.items ?? rowsresponse.data).length > PAGE_SIZE) {
        setCanLoadMore(true);
        setCurrentPageNumber(0);
      } else {
        setCanLoadMore(false);
      }

      if (rowsresponse.data.items ?? rowsresponse.data) {
        const objectArray: any[] = [];
        const idArray: any[] = [];

        if (
          initialValue &&
          !(rowsresponse.data.items ?? rowsresponse.data).find((x: { id: string | null }) => x.id == initialValue)
        ) {
          objectArray.push(initialValueObject);
          idArray.push(initialValue);
        }

        for (let index = 0; index < (rowsresponse.data.items ?? rowsresponse.data).length; index++) {
          objectArray.push((rowsresponse.data.items ?? rowsresponse.data)[index]);
          idArray.push((rowsresponse.data.items ?? rowsresponse.data)[index].id);
        }

        if (objectArray.length !== 0 || idArray.length !== 0) {
          setOptionObjects(objectArray);
          setAutocompleteOptions(idArray);
        }

        setTimeout(() => {
          setLoading(false);
        }, 100);
      } else {
        setAutocompleteOptions([]);
        setTimeout(() => {
          setLoading(false);
        }, 100);
      }
    })
    .catch(err => {
      vigoToast.error(err.message);
      setAutocompleteOptions([]);
      setTimeout(() => {
        setLoading(false);
      }, 100);
    });
}

/**
 * Fired when user clicks on the dropdown
 */
export function fetchFirst25RowsFromSource(
  serviceEndpoint: string,
  serviceLocation: string,
  setLoading: Dispatch<boolean>,
  useQueryTextAsFilter: boolean | undefined,
  queryColumns: string[] | undefined,
  displayName: string | undefined,
  inputValue: string | undefined,
  datasourceFilter: string | undefined,
  evaluateFilter: ((filter: string) => string) | undefined,
  sort: string,
  tenantId: string | undefined,
  partnerId: string | undefined,
  setCanLoadMore: Dispatch<boolean>,
  setCurrentPageNumber: Dispatch<number>,
  initialValue: string | null,
  initialValueObject: any | null,
  setOptionObjects: Dispatch<any[]>,
  setAutocompleteOptions: Dispatch<any[]>,
  vigoToast: any,
  PAGE_SIZE: number
) {
  if (serviceEndpoint == '' || serviceLocation == '') {
    return;
  }

  setLoading(true);

  getMasterDataRow(
    {
      serviceLocation: serviceLocation,
      endPoint: serviceEndpoint,
      filter: buildFilter(
        undefined,
        useQueryTextAsFilter,
        queryColumns,
        displayName,
        inputValue,
        datasourceFilter,
        evaluateFilter
      ),
      pageSize: PAGE_SIZE,
      pageNumber: 0,
      sort: sort,
      includeMetadata: true
    },
    tenantId,
    partnerId
  )
    .then(rowresponse => {
      if ((rowresponse.data.items ?? rowresponse.data).length >= PAGE_SIZE) {
        setCanLoadMore(true);
        setCurrentPageNumber(0);
      } else {
        setCanLoadMore(false);
      }

      const objectArray: any[] = [];
      const idArray: any[] = [];

      if (
        initialValue &&
        !(rowresponse.data.items ?? rowresponse.data).find((x: { id: string }) => x.id == initialValue)
      ) {
        objectArray.push(initialValueObject);
        idArray.push(initialValue);
      }

      for (let index = 0; index < (rowresponse.data.items ?? rowresponse.data).length; index++) {
        objectArray.push((rowresponse.data.items ?? rowresponse.data)[index]);
        idArray.push((rowresponse.data.items ?? rowresponse.data)[index].id);
      }

      setOptionObjects(objectArray);
      setAutocompleteOptions(idArray);
      setTimeout(() => {
        setLoading(false);
      }, 100);
    })
    .catch(err => {
      vigoToast.error(err.message);
      setCanLoadMore(false);
      setAutocompleteOptions([]);
      setOptionObjects([]);
      setTimeout(() => {
        setLoading(false);
      }, 100);
    });
}

/**
 * Fired when user reaches the bottom of the Autocomplete list AND there are more items to load
 */
export function loadMoreRows(
  setCurrentPageNumber: Dispatch<number>,
  currentPageNumber: number,
  setCanLoadMore: Dispatch<boolean>,
  serviceLocation: string,
  serviceEndpoint: string,
  useQueryTextAsFilter: boolean | undefined,
  queryColumns: string[] | undefined,
  displayName: string | undefined,
  inputValue: string | undefined,
  datasourceFilter: string | undefined,
  evaluateFilter: ((filter: string) => string) | undefined,
  sort: string,
  tenantId: string | undefined,
  partnerId: string | undefined,
  optionObjects: any[],
  autocompleteOptions: any[],
  initialValue: string | null,
  setOptionObjects: Dispatch<any[]>,
  setAutocompleteOptions: Dispatch<any[]>,
  vigoToast: any,
  PAGE_SIZE: number,
  setLoading: Dispatch<boolean>
) {
  return new Promise((resolve, reject) => {
    setLoading(true);

    getMasterDataRow(
      {
        serviceLocation: serviceLocation,
        endPoint: serviceEndpoint,
        filter: buildFilter(
          undefined,
          useQueryTextAsFilter,
          queryColumns,
          displayName,
          inputValue,
          datasourceFilter,
          evaluateFilter
        ),
        pageSize: PAGE_SIZE,
        pageNumber: currentPageNumber + 1,
        sort: sort,
        includeMetadata: true
      },
      tenantId,
      partnerId
    )
      .then(rowresponse => {
        const newRows = rowresponse.data.items ?? rowresponse.data;
        const hasMoreData = newRows.length >= PAGE_SIZE;

        // Update pagination state
        setCanLoadMore(hasMoreData);
        if (hasMoreData) {
          setCurrentPageNumber(currentPageNumber + 1);
        }

        // Append new page results to existing options (immutable pattern)
        const updatedObjects = [...optionObjects, ...newRows];
        const updatedOptions = [...autocompleteOptions, ...newRows.map((row: any) => row.id)];

        setOptionObjects(updatedObjects);
        setAutocompleteOptions(updatedOptions);
        resolve('');
        setLoading(false);
      })
      .catch(err => {
        vigoToast.error(err.message);
        setCanLoadMore(false);
        setOptionObjects([]);
        reject('');
        setLoading(false);
      });
  });
}
